import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { liveLink, liveLinkDisplay } from '@lib/livePolls/liveLinks';
import { cn } from '@lib/utils';
import type { LiveState } from '@models/api';
import { BigScreenQr, ReconnectingChip, TypedLink } from './BigScreenParts';
import { useStageBox } from './BigScreenStage';

/** Clear space the QR keeps from the stage's edge. */
const EDGE = 28;

const clampShare = (value: number) => Math.min(1, Math.max(0, value));

export interface BigScreenCoverProps {
  state: LiveState;
  image: string;
  qr: { x: number; y: number };
  reconnecting?: boolean;
  /** The host view's preview only: the QR can be dragged, and this saves where it lands. */
  onMoveQr?: (qr: { x: number; y: number }) => void;
  /** The picture did not load: the screen falls back to the join screen rather than a black stage. */
  onImageError?: () => void;
}

/**
 * Screen 1 with the host's picture: the picture whole (never cropped, black bars on another shape of screen), and
 * only the app's QR with the typed link on top. Replaces B1, before Start only: from Start the room is on B1b.
 */
export function BigScreenCover({
  state,
  image,
  qr,
  reconnecting = false,
  onMoveQr,
  onImageError,
}: BigScreenCoverProps) {
  const { width, height, scale } = useStageBox();
  const tile = useRef<HTMLDivElement>(null);
  const [dragged, setDragged] = useState<{ x: number; y: number } | null>(null);
  const position = dragged ?? qr;

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const el = tile.current;
    if (!onMoveQr || !el) return;
    event.preventDefault();
    el.setPointerCapture(event.pointerId);
    const roomX = Math.max(1, width - 2 * EDGE - el.offsetWidth);
    const roomY = Math.max(1, height - 2 * EDGE - el.offsetHeight);
    const start = { clientX: event.clientX, clientY: event.clientY, ...position };
    let latest = position;
    let moved = false;
    const move = (next: PointerEvent) => {
      moved = true;
      latest = {
        x: clampShare(start.x + (next.clientX - start.clientX) / scale / roomX),
        y: clampShare(start.y + (next.clientY - start.clientY) / scale / roomY),
      };
      setDragged(latest);
    };
    // A tap saves nothing, and a gesture the browser cancelled puts the QR back where it was.
    const finish = (end: PointerEvent) => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', finish);
      el.removeEventListener('pointercancel', finish);
      if (moved && end.type === 'pointerup') onMoveQr(latest);
      setDragged(null);
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', finish);
    el.addEventListener('pointercancel', finish);
  };

  // The chip keeps clear of the QR when it sits top left.
  const qrTopLeft = position.x < 0.35 && position.y < 0.35;
  return (
    <div className="relative h-full w-full overflow-hidden bg-black" data-testid="big-screen-cover">
      <img
        src={image}
        alt=""
        draggable={false}
        onError={onImageError}
        className="block h-full w-full object-contain"
        data-testid="big-screen-cover-image"
      />
      {reconnecting ? (
        <div className={cn('absolute top-7', qrTopLeft ? 'right-7' : 'left-7')}>
          <ReconnectingChip />
        </div>
      ) : null}
      <div
        ref={tile}
        onPointerDown={onMoveQr ? onPointerDown : undefined}
        className={cn(
          'absolute flex w-[264px] flex-col items-center gap-0.5 rounded-3xl bg-white px-1.5 pt-1.5 pb-3.5 text-[#0D0D0D] select-none',
          onMoveQr && 'cursor-grab touch-none active:cursor-grabbing',
        )}
        style={{
          left: `calc(${EDGE}px + ${position.x} * (100% - ${2 * EDGE}px))`,
          top: `calc(${EDGE}px + ${position.y} * (100% - ${2 * EDGE}px))`,
          transform: `translate(${-position.x * 100}%, ${-position.y * 100}%)`,
        }}
        data-testid="big-screen-cover-qr"
      >
        <div className="pointer-events-none">
          <BigScreenQr link={liveLink(state.live_code)} size={252} />
        </div>
        <p className="text-center font-fira text-[15px] font-semibold leading-5">
          <TypedLink display={liveLinkDisplay(state.live_code)} />
        </p>
      </div>
    </div>
  );
}
